import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { QrCode, Smartphone, BarChart3, ShieldCheck, Zap, Users, CheckCircle2, ArrowRight, Globe, Ticket, Star } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/Button';
// Animation variants
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
export function LandingPage() {
  return <div className="min-h-screen bg-slate-50 font-sans text-slate-900 overflow-x-hidden" data-id="element-404">
      <Navbar data-id="element-405" />

      {/* HERO SECTION */}
      <section className="relative min-h-screen flex items-center pt-20 overflow-hidden bg-slate-950" data-id="element-406">
        {/* Abstract Background Elements */}
        <div className="absolute inset-0 overflow-hidden" data-id="element-407">
          <div className="absolute -top-[20%] -right-[10%] w-[70vw] h-[70vw] rounded-full bg-indigo-600/20 blur-[100px]" data-id="element-408" />
          <div className="absolute top-[40%] -left-[10%] w-[50vw] h-[50vw] rounded-full bg-purple-600/10 blur-[100px]" data-id="element-409" />
          <div className="absolute bottom-[10%] right-[20%] w-[30vw] h-[30vw] rounded-full bg-emerald-500/10 blur-[80px]" data-id="element-410" />
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1492684223066-81342ee5ff30?q=80&w=2070&auto=format&fit=crop')] bg-cover bg-center opacity-10 mix-blend-overlay" data-id="element-411" />
        </div>

        <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-412">
          <div className="max-w-4xl mx-auto text-center" data-id="element-413">
            <motion.div initial="hidden" animate="visible" variants={staggerContainer} data-id="element-414">
              <motion.div variants={fadeInUp} className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-900/50 border border-indigo-700/50 text-indigo-300 text-sm font-medium mb-8 backdrop-blur-sm" data-id="element-415">
                <Zap size={14} className="text-yellow-400" data-id="element-416" />
                <span data-id="element-417">La nueva era de gestión omnicanal</span>
              </motion.div>

              <motion.h1 variants={fadeInUp} className="text-5xl md:text-7xl font-bold text-white tracking-tight leading-[1.1] mb-6" data-id="element-418">
                Control total <br data-id="element-419" />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 via-purple-400 to-emerald-400" data-id="element-420">
                  multiplataforma
                </span>
              </motion.h1>

              <motion.p variants={fadeInUp} className="text-lg md:text-xl text-slate-400 max-w-2xl mx-auto leading-relaxed" data-id="element-421">
                Gestionamos todas las plataformas y canales para una gestión
                integral de acuerdo a cada necesidad. Administramos desde
                información general hasta venta de tickets, recaudaciones,
                acreditaciones, controles de acceso, tiendas virtuales y
                presenciales, programas de fidelización y decenas de necesidades
                adicionales. Todo adaptado a la imagen e ideología de cada uno.
              </motion.p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* SERVICES SECTION */}
      <section id="servicios" className="py-24 bg-white relative" data-id="element-422">
        <div className="container mx-auto px-4 md:px-6" data-id="element-423">
          <div className="text-center mb-20" data-id="element-424">
            <h2 className="text-3xl md:text-5xl font-bold text-slate-900 mb-4" data-id="element-425">
              Soluciones Integrales
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto" data-id="element-426">
              Un ecosistema completo de software, plataformas y equipamiento
              para organizadores, clubes e instituciones.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-427">
            {/* Service 1 */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="group p-8 rounded-2xl bg-slate-50 border border-slate-100 hover:border-indigo-100 hover:shadow-xl hover:shadow-indigo-900/5 transition-all duration-300 flex flex-col h-full" data-id="element-428">
              <div className="w-14 h-14 rounded-xl bg-indigo-100 flex items-center justify-center mb-6 group-hover:bg-indigo-600 transition-colors duration-300" data-id="element-429">
                <Ticket className="w-7 h-7 text-indigo-600 group-hover:text-white transition-colors duration-300" data-id="element-430" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-3" data-id="element-431">
                Ticketing & Venta Multicanal
              </h3>
              <p className="text-slate-600 leading-relaxed mb-6 flex-grow" data-id="element-432">
                Venta de entradas a través de web, app móvil, kioscos de
                autoatención, WhatsApp y boleterías físicas. Reportes
                financieros en tiempo real.
              </p>
              <ul className="space-y-2" data-id="element-433">
                <li className="flex items-start gap-2 text-sm text-slate-700" data-id="element-434">
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0 text-emerald-500 mt-0.5" data-id="element-435" />{' '}
                  Venta en múltiples canales simultáneos
                </li>
                <li className="flex items-start gap-2 text-sm text-slate-700" data-id="element-436">
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0 text-emerald-500 mt-0.5" data-id="element-437" />{' '}
                  Pasarelas de pago integradas
                </li>
                <li className="flex items-start gap-2 text-sm text-slate-700" data-id="element-438">
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0 text-emerald-500 mt-0.5" data-id="element-439" />{' '}
                  Tickets dinámicos y transferibles
                </li>
                <li className="flex items-start gap-2 text-sm text-slate-700" data-id="element-440">
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0 text-emerald-500 mt-0.5" data-id="element-441" />{' '}
                  Reportes financieros en tiempo real
                </li>
              </ul>
            </motion.div>

            {/* Service 2 */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.15
          }} className="group p-8 rounded-2xl bg-slate-50 border border-slate-100 hover:border-purple-100 hover:shadow-xl hover:shadow-purple-900/5 transition-all duration-300 flex flex-col h-full" data-id="element-442">
              <div className="w-14 h-14 rounded-xl bg-purple-100 flex items-center justify-center mb-6 group-hover:bg-purple-600 transition-colors duration-300" data-id="element-443">
                <ShieldCheck className="w-7 h-7 text-purple-600 group-hover:text-white transition-colors duration-300" data-id="element-444" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-3" data-id="element-445">
                Control de Acceso
              </h3>
              <p className="text-slate-600 leading-relaxed mb-6 flex-grow" data-id="element-446">
                Validación ultra-rápida con lectores QR, torniquetes, molinetes
                y dispositivos portátiles. Control de aforo en tiempo real.
              </p>
              <ul className="space-y-2" data-id="element-447">
                <li className="flex items-start gap-2 text-sm text-slate-700" data-id="element-448">
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0 text-emerald-500 mt-0.5" data-id="element-449" />{' '}
                  Lectores QR de alta velocidad
                </li>
                <li className="flex items-start gap-2 text-sm text-slate-700" data-id="element-450">
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0 text-emerald-500 mt-0.5" data-id="element-451" />{' '}
                  Torniquetes y molinetes integrados
                </li>
                <li className="flex items-start gap-2 text-sm text-slate-700" data-id="element-452">
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0 text-emerald-500 mt-0.5" data-id="element-453" />{' '}
                  Control de aforo en tiempo real
                </li>
                <li className="flex items-start gap-2 text-sm text-slate-700" data-id="element-454">
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0 text-emerald-500 mt-0.5" data-id="element-455" />{' '}
                  Personal capacitado y equipamiento
                </li>
              </ul>
            </motion.div>

            {/* Service 3 */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="group p-8 rounded-2xl bg-slate-50 border border-slate-100 hover:border-emerald-100 hover:shadow-xl hover:shadow-emerald-900/5 transition-all duration-300 flex flex-col h-full" data-id="element-456">
              <div className="w-14 h-14 rounded-xl bg-emerald-100 flex items-center justify-center mb-6 group-hover:bg-emerald-500 transition-colors duration-300" data-id="element-457">
                <Users className="w-7 h-7 text-emerald-600 group-hover:text-white transition-colors duration-300" data-id="element-458" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-3" data-id="element-459">
                Gestión Institucional
              </h3>
              <p className="text-slate-600 leading-relaxed mb-6 flex-grow" data-id="element-460">
                Administración integral de socios, abonos, credenciales
                digitales, escuelas deportivas y acreditaciones.
              </p>
              <ul className="space-y-2" data-id="element-461">
                <li className="flex items-start gap-2 text-sm text-slate-700" data-id="element-462">
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0 text-emerald-500 mt-0.5" data-id="element-463" />{' '}
                  Gestión de socios y abonos
                </li>
                <li className="flex items-start gap-2 text-sm text-slate-700" data-id="element-464">
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0 text-emerald-500 mt-0.5" data-id="element-465" />{' '}
                  Credenciales y carnet digital
                </li>
                <li className="flex items-start gap-2 text-sm text-slate-700" data-id="element-466">
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0 text-emerald-500 mt-0.5" data-id="element-467" />{' '}
                  Administración de escuelas deportivas
                </li>
                <li className="flex items-start gap-2 text-sm text-slate-700" data-id="element-468">
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0 text-emerald-500 mt-0.5" data-id="element-469" />{' '}
                  Acreditaciones de prensa y staff
                </li>
              </ul>
            </motion.div>

            {/* Service 4 */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.25
          }} className="group p-8 rounded-2xl bg-slate-50 border border-slate-100 hover:border-sky-100 hover:shadow-xl hover:shadow-sky-900/5 transition-all duration-300 flex flex-col h-full" data-id="element-470">
              <div className="w-14 h-14 rounded-xl bg-sky-100 flex items-center justify-center mb-6 group-hover:bg-sky-600 transition-colors duration-300" data-id="element-471">
                <Smartphone className="w-7 h-7 text-sky-600 group-hover:text-white transition-colors duration-300" data-id="element-472" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-3" data-id="element-473">
                Apps & Plataformas White-Label
              </h3>
              <p className="text-slate-600 leading-relaxed mb-6 flex-grow" data-id="element-474">
                Portal web y app móvil iOS/Android personalizados con la imagen
                y colores de tu organización.
              </p>
              <ul className="space-y-2" data-id="element-475">
                <li className="flex items-start gap-2 text-sm text-slate-700" data-id="element-476">
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0 text-emerald-500 mt-0.5" data-id="element-477" />{' '}
                  App móvil personalizada (iOS & Android)
                </li>
                <li className="flex items-start gap-2 text-sm text-slate-700" data-id="element-478">
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0 text-emerald-500 mt-0.5" data-id="element-479" />{' '}
                  Portal web institucional
                </li>
                <li className="flex items-start gap-2 text-sm text-slate-700" data-id="element-480">
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0 text-emerald-500 mt-0.5" data-id="element-481" />{' '}
                  Kioscos de autoatención
                </li>
                <li className="flex items-start gap-2 text-sm text-slate-700" data-id="element-482">
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0 text-emerald-500 mt-0.5" data-id="element-483" />{' '}
                  Notificaciones push y wallet digital
                </li>
              </ul>
            </motion.div>

            {/* Service 5 */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.3
          }} className="group p-8 rounded-2xl bg-slate-50 border border-slate-100 hover:border-amber-100 hover:shadow-xl hover:shadow-amber-900/5 transition-all duration-300 flex flex-col h-full" data-id="element-484">
              <div className="w-14 h-14 rounded-xl bg-amber-100 flex items-center justify-center mb-6 group-hover:bg-amber-500 transition-colors duration-300" data-id="element-485">
                <Star className="w-7 h-7 text-amber-600 group-hover:text-white transition-colors duration-300" data-id="element-486" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-3" data-id="element-487">
                Programas de Fidelización
              </h3>
              <p className="text-slate-600 leading-relaxed mb-6 flex-grow" data-id="element-488">
                Puntos, recompensas, niveles de membresía y beneficios
                exclusivos para construir relaciones duraderas con tu audiencia.
              </p>
              <ul className="space-y-2" data-id="element-489">
                <li className="flex items-start gap-2 text-sm text-slate-700" data-id="element-490">
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0 text-emerald-500 mt-0.5" data-id="element-491" />{' '}
                  Acumulación de puntos por compras y asistencia
                </li>
                <li className="flex items-start gap-2 text-sm text-slate-700" data-id="element-492">
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0 text-emerald-500 mt-0.5" data-id="element-493" />{' '}
                  Catálogo de recompensas canjeables
                </li>
                <li className="flex items-start gap-2 text-sm text-slate-700" data-id="element-494">
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0 text-emerald-500 mt-0.5" data-id="element-495" />{' '}
                  Niveles de membresía exclusivos
                </li>
                <li className="flex items-start gap-2 text-sm text-slate-700" data-id="element-496">
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0 text-emerald-500 mt-0.5" data-id="element-497" />{' '}
                  Comunicación segmentada por nivel
                </li>
              </ul>
              <a href="https://benefika.net/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 px-5 py-2.5 mt-6 bg-white hover:bg-orange-50 text-slate-900 border-2 border-orange-500 rounded-xl transition-colors font-medium text-sm group/btn" data-id="element-498">
                <img src="https://cdn.magicpatterns.com/uploads/rGwNdcUQBXjqXzrggvXeFv/logo_benefica_positivo.png" alt="Benefika" className="h-4 w-auto" data-id="element-499" />
                <span data-id="element-500">
                  Ir a <strong data-id="element-501">Benefika</strong>, nuestra plataforma de
                  fidelización.
                </span>
                <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" data-id="element-502" />
              </a>
            </motion.div>

            {/* Service 6 */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.35
          }} className="group p-8 rounded-2xl bg-slate-50 border border-slate-100 hover:border-rose-100 hover:shadow-xl hover:shadow-rose-900/5 transition-all duration-300 flex flex-col h-full" data-id="element-503">
              <div className="w-14 h-14 rounded-xl bg-rose-100 flex items-center justify-center mb-6 group-hover:bg-rose-600 transition-colors duration-300" data-id="element-504">
                <Zap className="w-7 h-7 text-rose-600 group-hover:text-white transition-colors duration-300" data-id="element-505" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-3" data-id="element-506">
                Eventos & Operaciones
              </h3>
              <p className="text-slate-600 leading-relaxed mb-6 flex-grow" data-id="element-507">
                Gestión integral de eventos de cualquier escala con equipamiento
                profesional y soporte logístico incluido.
              </p>
              <ul className="space-y-2" data-id="element-508">
                <li className="flex items-start gap-2 text-sm text-slate-700" data-id="element-509">
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0 text-emerald-500 mt-0.5" data-id="element-510" />{' '}
                  Desde pequeños eventos hasta estadios
                </li>
                <li className="flex items-start gap-2 text-sm text-slate-700" data-id="element-511">
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0 text-emerald-500 mt-0.5" data-id="element-512" />{' '}
                  Equipamiento completo de boletería
                </li>

                <li className="flex items-start gap-2 text-sm text-slate-700" data-id="element-513">
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0 text-emerald-500 mt-0.5" data-id="element-514" />{' '}
                  Radios y logística operativa
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="py-24 bg-white" data-id="element-515">
        <div className="container mx-auto px-4 md:px-6" data-id="element-516">
          <div className="bg-slate-950 rounded-3xl p-8 md:p-16 text-center relative overflow-hidden" data-id="element-517">
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0" data-id="element-518">
              <div className="absolute -top-[50%] -left-[20%] w-[800px] h-[800px] bg-indigo-600/20 rounded-full blur-[120px]" data-id="element-519" />
              <div className="absolute -bottom-[50%] -right-[20%] w-[800px] h-[800px] bg-emerald-500/10 rounded-full blur-[120px]" data-id="element-520" />
            </div>

            <div className="relative z-10 max-w-3xl mx-auto" data-id="element-521">
              <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" data-id="element-522">
                ¿Listo para llevar tu organización o idea al siguiente nivel?
              </h2>
              <p className="text-slate-400 text-lg mb-10" data-id="element-523">
                Únete a los cientos de organizadores, empresas e instituciones
                que confían en TicketMaestro.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-524">
                <Button variant="primary" size="lg" onClick={() => window.location.href = 'mailto:contacto@ticketmaestro.net'} data-id="element-525">
                  Agendar una reunión
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer data-id="element-526" />
    </div>;
}